import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, ShieldCheck, Heart, Upload, X as XIcon, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = [
  "Salvation",
  "Healing",
  "Provision",
  "Breakthrough",
  "Family",
  "Marriage",
  "Children",
  "Career / Studies",
  "Deliverance",
  "Travel / Visa",
  "Answered Prayer",
  "General",
];

const SHARE_MODES = [
  {
    id: "private",
    label: "Keep this between me &amp; the church",
    hint: "Only our pastoral team will see your testimony. Nothing public.",
  },
  {
    id: "named",
    label: "Share with my name",
    hint: "We&apos;ll display your first name + last initial (and photo, if provided).",
  },
  {
    id: "anonymous",
    label: "Share but keep me anonymous",
    hint: "We&apos;ll display the testimony as &ldquo;Anonymous&rdquo;. No name, no photo shown publicly.",
  },
];

export default function Testimony() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    share_mode: "private",
    category: "General",
    testimony: "",
    image_url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image too large (max 5 MB).");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/testimonies/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_url", r.data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.phone) delete payload.phone;
      if (!payload.image_url) delete payload.image_url;
      await api.post("/testimonies", payload);
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="testimony-thanks" className="min-h-[80vh] flex items-center justify-center px-6 py-32">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-xl text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#ff5c00]/15 mb-7">
            <Sparkles className="w-7 h-7 text-[#ff5c00]" />
          </div>
          <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#ff5c00] mb-3">All glory to God</p>
          <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter mb-5">Your testimony is received.</h1>
          <p className="text-zinc-300 leading-relaxed mb-8">
            Thank you for sharing what God has done. We have received your testimony and will be in touch.
            Your story will strengthen another believer&apos;s faith.
          </p>
          <button
            data-testid="testimony-submit-another"
            onClick={() => {
              setSubmitted(false);
              setForm({ first_name: "", last_name: "", email: "", phone: "", share_mode: "private", category: "General", testimony: "", image_url: "" });
            }}
            className="font-mono-accent text-xs tracking-[0.18em] uppercase text-zinc-400 hover:text-[#ff5c00] inline-flex items-center gap-2"
          >
            <Heart className="w-3 h-3" /> Share Another Testimony
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="testimony-page" className="min-h-screen pt-24 pb-24">
      {/* Hero */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#ff5c00]/8 via-transparent to-transparent" />
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[#ff5c00] mb-4">
            Submit a Testimony
          </p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase tracking-tighter font-bold leading-[0.95] mb-6">
            Tell us what <br className="hidden md:block" />
            <span className="text-[#ff5c00]">God has done</span>
          </h1>
          <p className="max-w-2xl text-zinc-300 text-lg leading-relaxed">
            Share the wondrous works of God in your life with His people. When you share your testimony,
            we return all the glory to God — and with your permission, we&apos;ll share it to strengthen the
            faith of others believing for their own miracle.
          </p>
          <blockquote className="mt-10 border-l-2 border-[#ff5c00] pl-6 max-w-2xl text-zinc-400 italic">
            &ldquo;The Lord has done great things for us; whereof we are glad.&rdquo;
            <span className="block mt-2 font-mono-accent text-[10px] tracking-[0.3em] uppercase not-italic text-zinc-500">— Psalm 126:3</span>
          </blockquote>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="bg-[#0f0f14] border border-white/10 rounded-sm p-7 md:p-10">
          <div className="flex items-center gap-2 mb-7">
            <ShieldCheck className="w-4 h-4 text-[#ff5c00]" />
            <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              Your submission is encrypted &amp; private until you give permission to share.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-6" data-testid="testimony-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="First Name *">
                <input
                  required
                  type="text"
                  data-testid="testimony-first-name"
                  value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)}
                  className={inputCls}
                  placeholder="Esther"
                />
              </Field>
              <Field label="Last Name *">
                <input
                  required
                  type="text"
                  data-testid="testimony-last-name"
                  value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)}
                  className={inputCls}
                  placeholder="Adeyemi"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Phone (preferred)" hint="So our pastoral team can follow up.">
                <input
                  type="tel"
                  data-testid="testimony-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={inputCls}
                  placeholder="+971 ..."
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  type="email"
                  data-testid="testimony-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  className={inputCls}
                  placeholder="you@example.com"
                />
              </Field>
            </div>

            <Field label="What kind of testimony is this?">
              <select
                data-testid="testimony-category"
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className={inputCls}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Your Testimony *">
              <textarea
                required
                rows={9}
                data-testid="testimony-text"
                value={form.testimony}
                onChange={(e) => set("testimony", e.target.value)}
                className={`${inputCls} resize-y leading-relaxed`}
                placeholder="Share what God has done — be as specific as you&apos;d like. Your name will not be shared without your express consent below."
              />
            </Field>

            {/* Optional profile photo */}
            <Field label="Your photo (optional)" hint="A face on a testimony makes it real. Only displayed if you choose to share with your name.">
              {form.image_url ? (
                <div data-testid="testimony-image-preview" className="flex items-center gap-4 bg-[#0a0a0d] border border-white/10 rounded-sm p-3">
                  <img src={form.image_url} alt="" className="w-16 h-16 rounded-sm object-cover" />
                  <div className="flex-1">
                    <p className="text-xs text-zinc-300">Photo attached.</p>
                    <p className="text-[10px] text-zinc-500 font-mono-accent uppercase tracking-widest mt-0.5">Will appear if you choose &quot;Share with my name&quot;.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => set("image_url", "")}
                    data-testid="testimony-image-remove"
                    className="text-zinc-500 hover:text-red-400 p-2"
                    aria-label="Remove photo"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  data-testid="testimony-image-upload-label"
                  className={`flex items-center justify-center gap-2 bg-[#0a0a0d] border border-dashed border-white/15 hover:border-[#ff5c00]/60 rounded-sm px-4 py-5 cursor-pointer text-zinc-400 hover:text-white transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  <span className="font-mono-accent text-[11px] tracking-[0.18em] uppercase">
                    {uploading ? "Uploading…" : "Upload photo (max 5 MB)"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="testimony-image-input"
                    onChange={(e) => handleUpload(e.target.files?.[0])}
                    className="hidden"
                  />
                </label>
              )}
            </Field>

            {/* Share-mode selector — replaces the single checkbox */}
            <div data-testid="testimony-share-mode-group">
              <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-3 inline-block">
                May we share your testimony with the church?
              </span>
              <div className="space-y-3">
                {SHARE_MODES.map((m) => {
                  const active = form.share_mode === m.id;
                  return (
                    <label
                      key={m.id}
                      data-testid={`testimony-share-mode-${m.id}`}
                      className={`flex items-start gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${
                        active
                          ? "border-[#ff5c00]/60 bg-[#ff5c00]/5"
                          : "border-white/10 bg-[#0a0a0d] hover:border-white/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="share_mode"
                        value={m.id}
                        checked={active}
                        onChange={() => set("share_mode", m.id)}
                        className="mt-1 w-4 h-4 accent-[#ff5c00]"
                      />
                      <div>
                        <p
                          className="font-display text-sm uppercase tracking-tight text-white"
                          dangerouslySetInnerHTML={{ __html: m.label }}
                        />
                        <p
                          className="text-xs text-zinc-400 mt-1 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: m.hint }}
                        />
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {error && (
              <div data-testid="testimony-error" className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || uploading}
              data-testid="testimony-submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(255,92,0,0.6)]"
            >
              <Send className="w-3.5 h-3.5" /> {submitting ? "Sending…" : "Submit My Testimony"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-zinc-500 font-mono-accent tracking-widest uppercase mt-7">
          Or email us directly — testimonies@ugycdubai.org
        </p>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#0a0a0d] border border-white/10 focus:border-[#ff5c00] outline-none rounded-sm px-4 py-3 text-white placeholder:text-zinc-600 font-sans text-sm transition-colors";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-zinc-400 mb-2 inline-block">{label}</span>
      {children}
      {hint && <span className="block mt-1.5 text-[11px] text-zinc-500">{hint}</span>}
    </label>
  );
}
